<template>
    <div class="w-full flex flex-col flex-1">
        <div
            class="w-full h-14 rounded-t-lg bg-background-3 border border-interactive-11 flex justify-between items-center px-4 box-border">
            <span class="cursor-pointer" @click="previousStep">&lt;</span>
            <span>Payment details</span>
            <div></div>
        </div>
        <div class="w-full flex flex-col pr-4 pl-4 pb-4 flex-1">
            <div class="flex w-full flex-wrap justify-between">
                <input
                    v-model="first_name.value.value"
                    type="text"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    :class="[ !errors.first_name ? 'border-interactive-8' : 'border-[2px] border-interactive-3' ]"
                    placeholder="First name"
                />
                <input
                    v-model="last_name.value.value"
                    type="text"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="Last name"
                />
                <div v-if="errors.first_name" class="w-[48%] mt-3 text-interactive-3 text-body-7 flex flex-col">
                    {{ errors.first_name }}
                </div>
                <div
                    v-if="errors.last_name"
                    class="w-[48%] mt-3 text-interactive-3 text-body-7 flex flex-col text-right"
                    :class="[errors.first_name ? 'w-[48%]' : 'w-full']"
                >
                    {{ errors.last_name }}
                </div>
                <input
                    v-model="email.value.value"
                    type="email"
                    class="mt-4 w-full p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="Email"/>
                <div v-if="errors.email" class="w-full mt-3 text-interactive-3 text-body-7 flex flex-col">
                    {{ errors.email }}
                </div>
                <input
                    v-model="address.value.value"
                    type="text"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="Address"
                />
                <input
                    v-model="city.value.value"
                    type="text"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="City"
                />
                <div v-if="errors.address" class="w-[48%] mt-3 text-interactive-3 text-body-7 flex flex-col">
                    {{ errors.address }}
                </div>
                <div
                    v-if="errors.city"
                    class="w-[48%] mt-3 text-interactive-3 text-body-7 flex flex-col text-right"
                    :class="[errors.address ? 'w-[48%]' : 'w-full']"
                >
                    {{ errors.city }}
                </div>
                <div class="w-full"></div>
                <input
                    v-model="state.value.value"
                    type="text"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="State"
                />
                <input
                    v-model="zip.value.value"
                    type="number"
                    min="00000"
                    max="99999"
                    class="mt-4 w-[48%] p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                    placeholder="Zip"
                />
                <div v-if="errors.state" class="w-[48%] mt-3 text-interactive-3 text-body-7 flex flex-col text-left">
                    {{ errors.state }}
                </div>
                <div
                    v-if="errors.zip"
                    class="mt-3 text-interactive-3 text-body-7 flex flex-col text-right"
                    :class="[errors.state ? 'w-[48%]' : 'w-full']"
                >
                    {{ errors.zip }}
                </div>
            </div>
            <div class="flex-1"></div>
            <Button :disabled="!meta.valid" :label="donationButtonLabel" class="mt-4" @click="handleData"/>
        </div>
    </div>
</template>

<script setup>
import Button from "@/components/Button.vue";
import {computed} from "vue";
import {storeToRefs} from "pinia";
import {useField, useForm} from "vee-validate";
import * as yup from "yup";
import {useDonationStore} from "@/store/useDonationStore";

const {amountToBeDonated, billingInfo} = storeToRefs(useDonationStore());
const {nextStep, previousStep} = useDonationStore();

const donationButtonLabel = computed(() => {
    return `Donate $${amountToBeDonated.value}`
})

const validationSchema = yup.object().shape({
    first_name: yup
        .string()
        .required('The subject is required')
        .matches(/^[A-Za-z-'" ]*$/, 'Enter a valid first name')
        .label('First name'),
    last_name: yup
        .string()
        .required('The subject is required')
        .matches(/^[A-Za-z-'" ]*$/, 'Enter a valid last name')
        .label('First name'),
    email: yup
        .string()
        .email('Please enter a valid email')
        .required('The text is required')
        .label('Email'),
    address: yup
        .string()
        .required('The text is required')
        .label('Address'),
    city: yup
        .string()
        .required('The city is required')
        .matches(/^[A-Za-z-'" ]*$/, 'Invalid a valid city')
        .label('City'),
    state: yup
        .string()
        .required('The state is required')
        .matches(/^[A-Za-z-'" ]*$/, 'Invalid a valid state')
        .label('State'),
    zip: yup
        .string()
        .required('The zip code is required')
        .label('Zip'),
});

const {meta, validate} = useForm({
    initialValues: {
        first_name: '',
        last_name: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    },
    validationSchema,
});

const first_name = useField('first_name');
const last_name = useField('last_name');
const email = useField('email');
const address = useField('address');
const city = useField('city');
const state = useField('state');
const zip = useField('zip');

const errors = computed(() => {
    return {
        first_name: first_name.errorMessage.value ?? null,
        last_name: last_name.errorMessage.value ?? null,
        email: email.errorMessage.value ?? null,
        address: address.errorMessage.value ?? null,
        city: city.errorMessage.value ?? null,
        state: state.errorMessage.value ?? null,
        zip: zip.errorMessage.value ?? null,
    }
});

function handleData() {
    validate().then((success) => {
        if (success) {
            billingInfo.value = {
                first_name: first_name.value.value,
                last_name: last_name.value.value,
                email: email.value.value,
                shipping_address_1: address.value.value,
                shipping_address_2: '',
                shipping_city: city.value.value,
                shipping_state: state.value.value,
                shipping_zip: zip.value.value,
                billing_address_1: address.value.value,
                billing_address_2: '',
                billing_city: city.value.value,
                billing_state: state.value.value,
                billing_zip: zip.value.value,
            }
            nextStep();
        }
    });
}
</script>